import { useCallback } from 'react'
import { useI18n } from '../../i18n'
import type { CVLanguage, LanguageProficiency } from '../../types/cv'
import './editor.css'

export interface LanguagesEditorProps {
  data: CVLanguage[]
  onChange: (data: CVLanguage[]) => void
}

const PROFICIENCY_LEVELS: LanguageProficiency[] = [
  'native',
  'fluent',
  'advanced',
  'intermediate',
  'basic',
]

/**
 * Languages section editor
 */
export function LanguagesEditor({ data, onChange }: LanguagesEditorProps) {
  const { t } = useI18n()
  const handleAdd = useCallback(() => {
    const newEntry: CVLanguage = {
      id: crypto.randomUUID(),
      name: '',
      proficiency: 'intermediate',
      sortOrder: data.length,
    }
    onChange([...data, newEntry])
  }, [data, onChange])

  const handleRemove = useCallback(
    (id: string) => {
      onChange(data.filter((item) => item.id !== id))
    },
    [data, onChange]
  )

  const handleChange = useCallback(
    (id: string, field: keyof CVLanguage, value: string) => {
      onChange(data.map((item) => (item.id === id ? { ...item, [field]: value } : item)))
    },
    [data, onChange]
  )

  return (
    <>
      <ul className="editor-entry-list" aria-label={t('editor.lists.languages')}>
        {data.map((lang) => {
          const langLabel = lang.name || t('editor.entries.language')
          return (
            <li key={lang.id} className="editor-entry editor-entry-compact" aria-label={langLabel}>
              <div className="editor-grid editor-grid-languages">
                <div className="editor-field">
                  <label htmlFor={`lang-name-${lang.id}`}>{t('fields.language')}</label>
                  <input
                    id={`lang-name-${lang.id}`}
                    type="text"
                    value={lang.name}
                    onChange={(e) => handleChange(lang.id, 'name', e.target.value)}
                    placeholder={t('placeholders.language')}
                  />
                </div>

                <div className="editor-field">
                  <label htmlFor={`lang-proficiency-${lang.id}`}>{t('fields.proficiency')}</label>
                  <select
                    id={`lang-proficiency-${lang.id}`}
                    value={lang.proficiency}
                    onChange={(e) => handleChange(lang.id, 'proficiency', e.target.value)}
                  >
                    {PROFICIENCY_LEVELS.map((level) => (
                      <option key={level} value={level}>
                        {t(`cv.languageProficiency.${level}`)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="editor-field editor-field-action">
                  <button
                    type="button"
                    className="editor-btn editor-btn-danger editor-btn-icon"
                    onClick={() => handleRemove(lang.id)}
                    aria-label={t('actions.remove', { item: langLabel })}
                  >
                    ×
                  </button>
                </div>
              </div>
            </li>
          )
        })}
      </ul>

      <button type="button" className="editor-add-btn" onClick={handleAdd}>
        <span aria-hidden="true">+</span> {t('actions.addLanguage')}
      </button>
    </>
  )
}
